import type { FreedTime, TodayBrief } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { ClockIcon, PlayIcon, XIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useWorkspace } from '@/components/workspace'
import { queryKeys, useRunTool } from '@/lib/items'
import { attention } from '@/lib/styles'
import { freedHeading, freedNote, freedReason } from '@/lib/today'
import { cn } from '@/lib/utils'
import { PriorityChip } from './priority-chip'

/** Full-width and tall enough for a thumb on phones; a normal button from `sm` up. */
const THUMB = 'h-10 w-full sm:h-8 sm:w-auto'

/**
 * Meeting time that became free today, and what hugo's planner would do in it (decision 105):
 * late work first, then priority, due date and fit. "Do it now" puts an item in Now on the board
 * (`item.move`, undoable); "Dismiss" stops suggesting (`today.dismiss_freed`). Rows open the
 * details.
 */
export function FreedTimeCard({
  freed,
  date,
  timeZone,
}: {
  freed: FreedTime
  date: string
  timeZone: string
}) {
  const titleId = useId()
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const { openDetails } = useWorkspace()
  const [busy, setBusy] = useState<string | null>(null)

  const start = async (itemId: string) => {
    setBusy(itemId)
    await run('item.move', { id: itemId, column: 'now' }, { done: 'In Now: being done first' })
    setBusy(null)
  }
  const dismiss = async () => {
    setBusy('dismiss')
    await queryClient.cancelQueries({ queryKey: queryKeys.today })
    const before = queryClient.getQueryData<TodayBrief>(queryKeys.today)
    queryClient.setQueryData<TodayBrief>(queryKeys.today, (old) =>
      old ? { ...old, freedTime: null } : old,
    )
    const result = await run(
      'today.dismiss_freed',
      { key: freed.key },
      { done: 'Dismissed', failed: 'Could not dismiss' },
    )
    if (result === null && before) queryClient.setQueryData(queryKeys.today, before)
    setBusy(null)
  }

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-xl border p-4"
      data-testid="freed-time"
      data-key={freed.key}
    >
      <div className="flex items-start gap-2">
        <ClockIcon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', attention.icon)} />
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={titleId} className="text-sm font-medium" data-testid="freed-time-heading">
            {freedHeading(freed, date, timeZone)}
          </h2>
          <p className="text-sm break-words text-muted-foreground" data-testid="freed-time-reason">
            {freedReason(freed.title)}
            {freed.items.length > 0 ? ' Use it for:' : ' Nothing waiting fits in it.'}
          </p>
        </div>
      </div>
      {freed.items.length > 0 ? (
        <ul className="divide-y overflow-hidden rounded-lg border">
          {freed.items.map(({ item, minutes }) => (
            <li
              key={item.id}
              className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center"
              data-testid="freed-time-item"
              data-item-id={item.id}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 flex-col items-start gap-0.5 rounded-md text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                onClick={() => openDetails(item.id)}
              >
                <span className="flex max-w-full items-center gap-2">
                  <span className="truncate text-sm font-medium">{item.title}</span>
                  <PriorityChip priority={item.priority} />
                </span>
                <span className="text-xs text-muted-foreground" data-testid="freed-time-note">
                  {item.plan
                    ? freedNote(minutes, item.plan)
                    : freedNote(minutes, { minutes, status: 'on_track' })}
                </span>
              </button>
              <Button
                variant="outline"
                className={THUMB}
                disabled={busy !== null}
                onClick={() => void start(item.id)}
              >
                {busy === item.id ? (
                  <Spinner aria-hidden="true" />
                ) : (
                  <PlayIcon aria-hidden="true" />
                )}
                Do it now
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <div>
        <Button
          variant="ghost"
          className={THUMB}
          disabled={busy !== null}
          onClick={() => void dismiss()}
        >
          {busy === 'dismiss' ? <Spinner aria-hidden="true" /> : <XIcon aria-hidden="true" />}
          Dismiss
        </Button>
      </div>
    </section>
  )
}
